/**
 * aides-collectivites.ts
 * ─────────────────────────────────────────────────────────────────
 * Configuration centralisée des pages "stubs" /collectivites/aides/[slug].
 *
 * Audience : maîtres d'ouvrage publics (communes, EPCI, départements,
 * régions, hôpitaux, écoles, bailleurs sociaux, établissements publics).
 */

import type { AideStub } from './aides-particuliers';

export const AIDES_COLLECTIVITES: AideStub[] = [
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'fonds-vert',
    iconName: 'sparkles',
    eyebrow: "Fonds d'accélération de la transition écologique",
    title: 'Fonds vert',
    shortLabel: 'Fonds vert',
    lead: "Un fonds de l'État pour vos projets de transition écologique : rénovation des bâtiments publics, énergies renouvelables, recyclage foncier. Doté chaque année, instruit en préfecture.",
    amount: 'Subvention',
    amountLabel: 'Taux variable selon thématique et territoire',
    keyPoints: [
      {
        iconName: 'landmark',
        label: 'Instruction préfectorale',
        description: "Dépôt du dossier sur la plateforme « Démarches simplifiées » ou « Aides territoires », instruction par la préfecture de département ou la DREAL selon l'axe.",
      },
      {
        iconName: 'home',
        label: 'Rénovation des bâtiments publics',
        description: "L'un des axes phares : isolation, changement de système thermique, ventilation, pilotage sur écoles, mairies, gymnases, médiathèques, EHPAD publics.",
      },
      {
        iconName: 'gauge',
        label: 'Cumulable avec aides et dotations',
        description: "Cumul possible avec les aides travaux, les dotations d'investissement de l'État, pour atteindre un plan de financement équilibré.",
      },
      {
        iconName: 'file-text',
        label: 'Pièces : projet, estimatif, plan de financement',
        description: "Dossier-type : note d'opportunité, estimatif détaillé, plan de financement prévisionnel, calendrier, indicateurs de résultat.",
      },
    ],
    conditions: [
      "Maître d'ouvrage éligible : commune, EPCI, syndicat mixte, département, région, établissement public",
      "Projet entrant dans l'un des axes thématiques publiés par la circulaire annuelle (rénovation, friches, EnR, mobilité, biodiversité…)",
      "Dépôt du dossier avant tout engagement juridique des travaux",
      "Plan de financement bouclé avec autofinancement minimum (variable selon les axes, généralement 20 % du HT)",
      "Engagement sur des indicateurs de suivi (gain énergétique attendu, surface, etc.)",
    ],
    cumulableWith: ['Aides collectivités', 'DETR / DSIL', 'Banque des Territoires', 'Fonds chaleur ADEME', 'Aides régionales'],
    faq: [
      {
        question: 'Qui peut déposer un dossier Fonds vert ?',
        answer:
          "Les communes, EPCI, syndicats mixtes, départements, régions, ainsi que certains établissements publics. Les particuliers et entreprises privées ne sont pas éligibles directement. Le dépôt se fait via la plateforme nationale ou en préfecture.",
      },
      {
        question: "Quel taux d'aide attendre ?",
        answer:
          "Variable selon l'axe et le territoire. La circulaire annuelle précise les plafonds et taux par thématique. Certains projets en zones cibles (quartiers prioritaires, ruralité) peuvent bénéficier d'une majoration.",
      },
      {
        question: "Le Fonds vert est-il cumulable avec les aides travaux ?",
        answer:
          "Oui, le cumul est possible et fréquent. Le programme dédié finance le diagnostic et l'assistance, les aides travaux financent les travaux d'efficacité énergétique, et le Fonds vert apporte la subvention principale.",
      },
    ],
    ctaLabel: 'Identifier mes aides Fonds vert',
    ctaHref: '/collectivites/diagnostic-cee',
    secondaryCta: { label: 'Être rappelé(e)', href: '/contact?source=collectivites&besoin=fonds-vert' },
  },
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'detr-dsil',
    iconName: 'landmark',
    eyebrow: "Dotations d'investissement de l'État",
    title: 'DETR & DSIL',
    shortLabel: 'DETR / DSIL',
    lead: "Deux dotations de l'État pour financer vos équipements : rénovation des bâtiments publics, transition énergétique. Pour les communes et intercommunalités.",
    amount: 'Subvention',
    amountLabel: "Cofinancement État, taux selon préfecture",
    keyPoints: [
      {
        iconName: 'landmark',
        label: 'DETR — milieu rural prioritaire',
        description: "Communes et EPCI ruraux selon des critères de population et de potentiel financier. Liste publiée chaque année par arrêté préfectoral.",
      },
      {
        iconName: 'building',
        label: 'DSIL — tous territoires',
        description: "Ouverte à toutes les communes et EPCI. Priorités définies par circulaire ministérielle annuelle (rénovation thermique, mobilités, sécurité…).",
      },
      {
        iconName: 'file-text',
        label: 'Commission préfectorale',
        description: "Dossiers instruits par la préfecture et soumis à la commission des élus (pour la DETR) ou à la décision du préfet (pour la DSIL).",
      },
      {
        iconName: 'gauge',
        label: 'Cumulable avec Fonds vert',
        description: "Cumul DETR/DSIL + Fonds vert + aides travaux possible pour optimiser le plan de financement.",
      },
    ],
    conditions: [
      "Maître d'ouvrage éligible : commune, EPCI (DETR : selon critères de ruralité ; DSIL : tous)",
      "Projet relevant des priorités fixées par la circulaire annuelle (rénovation thermique, mobilités, sécurité, équipements scolaires/sportifs)",
      "Dépôt du dossier avant tout engagement juridique des travaux",
      "Pièces : note d'opportunité, plan de financement, calendrier, justificatif de la délibération",
      "Respect des règles de la commande publique",
    ],
    cumulableWith: ['Fonds vert', 'Aides collectivités', 'Banque des Territoires', 'Fonds chaleur ADEME', 'Aides régionales'],
    faq: [
      {
        question: 'Quelle différence entre DETR et DSIL ?',
        answer:
          "La DETR cible prioritairement les communes et EPCI ruraux selon des critères de population et de potentiel financier — la liste des collectivités éligibles est publiée chaque année en préfecture. La DSIL est ouverte à toutes les communes et EPCI, sans condition de ruralité, sur des thématiques prioritaires définies nationalement.",
      },
      {
        question: "Comment se déroule l'instruction ?",
        answer:
          "Pour la DETR, l'instruction est faite par la préfecture et soumise à une commission des élus qui rend un avis avant la décision préfectorale. Pour la DSIL, la décision relève directement du préfet de département ou de région selon le seuil financier.",
      },
      {
        question: "Quel taux moyen d'aide ?",
        answer:
          "Le taux d'aide est variable et fixé par le préfet. Il dépend de la qualité du projet, des priorités locales et de l'enveloppe disponible. Le taux maximum d'aide publique (toutes subventions confondues) est encadré par l'autofinancement minimum exigé pour le maître d'ouvrage.",
      },
    ],
    ctaLabel: 'Étudier mon dossier DETR/DSIL',
    ctaHref: '/collectivites/diagnostic-cee',
    secondaryCta: { label: 'Être rappelé(e)', href: '/contact?source=collectivites&besoin=detr-dsil' },
  },
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'cee-collectivites',
    iconName: 'sparkles',
    eyebrow: 'Programmes collectivités ACTEE et SEQUOIA, pilotés par la FNCCR',
    title: 'CEE collectivités',
    shortLabel: 'CEE ACTEE',
    lead: "Des programmes nationaux pour vous aider à financer l'ingénierie de vos projets : diagnostic, assistance à maîtrise d'ouvrage, rénovation des écoles.",
    amount: 'Selon fiche & opération',
    amountLabel: 'Aide travaux + financement assistance via ACTEE',
    keyPoints: [
      {
        iconName: 'sparkles',
        label: 'Catalogue travaux bâtiment',
        description: "Bâtiments publics : plusieurs fiches couvrent les travaux sur écoles, mairies, gymnases et autres bâtiments tertiaires publics.",
      },
      {
        iconName: 'users',
        label: 'Programme ACTEE — FNCCR',
        description: "Soutient l'ingénierie, les diagnostics, le poste d'économe de flux et les groupements de commandes.",
      },
      {
        iconName: 'file-search',
        label: 'SEQUOIA pour rénovation lourde',
        description: "Sous-programme dédié à la rénovation des écoles primaires. Cofinance l'ingénierie et l'audit énergétique des groupes scolaires.",
      },
      {
        iconName: 'file-signature',
        label: 'Engagement avant signature',
        description: "Pour valoriser les aides travaux, la demande doit précéder la notification du marché de travaux.",
      },
    ],
    conditions: [
      "Maître d'ouvrage public (collectivité, EPCI, établissement public, bailleur social) éligible aux fiches bâtiment",
      "Travaux conformes aux exigences techniques de la fiche applicable (résistance thermique, classe énergétique, etc.)",
      "Demande d'aide travaux avant la notification du marché",
      "Pour ACTEE/SEQUOIA : candidature à un appel à manifestation d'intérêt FNCCR",
      "Conservation des pièces justificatives",
    ],
    cumulableWith: ['Fonds vert', 'DETR / DSIL', 'Banque des Territoires', 'Fonds chaleur ADEME', 'Aides régionales'],
    faq: [
      {
        question: "Qu'est-ce que le programme ACTEE ?",
        answer:
          "ACTEE est un programme piloté par la FNCCR qui finance l'ingénierie de la rénovation énergétique des bâtiments publics : diagnostics, audits, assistance à maîtrise d'ouvrage, poste d'économe de flux mutualisé. Il fonctionne par appels à manifestation d'intérêt successifs. Les aides travaux restent à mobiliser séparément.",
      },
      {
        question: "SEQUOIA, c'est différent d'ACTEE ?",
        answer:
          "SEQUOIA est un sous-programme d'ACTEE dédié spécifiquement à la rénovation des écoles primaires et groupes scolaires. Il prend en charge tout ou partie de l'ingénierie pré-opérationnelle (audit énergétique, programmation, AMO) pour les communes maîtres d'ouvrage.",
      },
      {
        question: 'Une collectivité peut-elle valoriser ses aides en direct ?',
        answer:
          "Oui. Une collectivité peut soit céder ses droits en échange d'une aide, soit s'inscrire elle-même au registre national pour les valoriser. La voie « cession » est la plus simple administrativement. La voie « valorisation directe » suppose une expertise interne et un volume significatif.",
      },
    ],
    ctaLabel: 'Diagnostic CEE collectivités',
    ctaHref: '/collectivites/diagnostic-cee',
    secondaryCta: { label: "Voir les fiches isolation tertiaire (BAT-EN)", href: '/cee/fiches?secteur=BAT' },
  },
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'banque-territoires',
    iconName: 'banknote',
    eyebrow: 'Caisse des Dépôts et Consignations',
    title: 'Prêts Banque des Territoires',
    shortLabel: 'Banque des Territoires',
    lead: "Des prêts à long terme et à taux bonifiés pour vos projets : rénovation énergétique, écoles, eau, mobilités. Proposés par la Caisse des Dépôts.",
    amount: 'Prêt à long terme',
    amountLabel: 'Indexé Livret A, jusqu\'à 40 ans selon enveloppe',
    keyPoints: [
      {
        iconName: 'banknote',
        label: 'GreenPrêt',
        description: "Prêt dédié à la rénovation énergétique du patrimoine bâti des collectivités. Durée longue, taux indexé sur le Livret A.",
      },
      {
        iconName: 'home',
        label: 'Édu-Prêt',
        description: "Construction, rénovation et équipement des écoles, collèges et lycées. Maître d'ouvrage : commune, EPCI, département, région.",
      },
      {
        iconName: 'droplet',
        label: 'Aqua-Prêt & Mobi-Prêt',
        description: "Aqua-Prêt finance l'eau, l'assainissement et la gestion des eaux pluviales. Mobi-Prêt finance les infrastructures de mobilité décarbonée.",
      },
      {
        iconName: 'users',
        label: 'Bailleurs sociaux et opérateurs',
        description: "Bailleurs sociaux, SEM, SPL, sociétés d'économie mixte ont aussi accès aux enveloppes de la Caisse des Dépôts.",
      },
    ],
    conditions: [
      "Personne morale éligible : collectivité, EPCI, syndicat mixte, établissement public, bailleur social, SEM/SPL selon l'enveloppe",
      "Projet éligible à l'enveloppe ciblée (rénovation énergétique pour GreenPrêt, scolaire pour Édu-Prêt, etc.)",
      'Capacité de remboursement justifiée par le plan pluriannuel d\'investissement et la capacité d\'autofinancement',
      "Délibération de l'assemblée délibérante autorisant l'emprunt",
      'Conformité à la réglementation budgétaire et comptable applicable',
    ],
    cumulableWith: ['Fonds vert', 'DETR / DSIL', 'Aides collectivités', 'Fonds chaleur ADEME', 'Aides régionales'],
    faq: [
      {
        question: 'Comment souscrire un prêt Banque des Territoires ?',
        answer:
          "Le processus passe par le portail banquedesterritoires.fr ou via le chargé de mission territorial. La collectivité doit produire : une délibération autorisant l'emprunt, un projet structuré (note technique, plan de financement, calendrier), et les éléments financiers (compte administratif, état de la dette, PPI).",
      },
      {
        question: "Quel est l'avantage par rapport à un prêt bancaire classique ?",
        answer:
          "Trois avantages : (1) la durée d'amortissement peut atteindre 40 ans, contre rarement plus de 20 ans en banque commerciale ; (2) le taux est indexé sur le Livret A (révision réglementée, lisibilité long terme) ; (3) les enveloppes sont fléchées sur des projets d'intérêt général, garantissant un alignement sur les enjeux de la collectivité.",
      },
      {
        question: 'Le prêt est-il cumulable avec les subventions ?',
        answer:
          "Oui, le prêt finance le reste à charge après cumul des subventions (Fonds vert, DETR/DSIL, aides travaux…). C'est la combinaison la plus fréquente : subventions sur la part de coût éligible + prêt Banque des Territoires sur le reste.",
      },
    ],
    ctaLabel: 'Étudier mon plan de financement',
    ctaHref: '/contact?source=collectivites&besoin=banque-territoires',
    secondaryCta: { label: 'Voir tous les dispositifs', href: '/collectivites/aides' },
  },
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'fonds-chaleur',
    iconName: 'flame',
    eyebrow: 'Programme ADEME',
    title: 'Fonds chaleur ADEME',
    shortLabel: 'Fonds chaleur',
    lead: "Une aide pour vos projets de chaleur renouvelable : réseaux de chaleur, biomasse, géothermie, solaire thermique. L'outil principal pour décarboner le chauffage de vos bâtiments.",
    amount: 'Subvention ADEME',
    amountLabel: 'Forfaitaire (€/MWh EnR) ou analytique selon projet',
    keyPoints: [
      {
        iconName: 'flame',
        label: 'Réseaux de chaleur renouvelables',
        description: "Création ou extension de réseaux de chaleur alimentés majoritairement par énergies renouvelables (biomasse, géothermie, solaire thermique, récupération de chaleur).",
      },
      {
        iconName: 'sparkles',
        label: 'Chaufferies biomasse publiques',
        description: "Chaufferies à plaquettes ou granulés pour bâtiments publics (écoles, gymnases, mairies, complexes sportifs).",
      },
      {
        iconName: 'thermometer',
        label: 'Géothermie et solaire thermique',
        description: "Pompes à chaleur géothermiques sur nappe ou sondes verticales, capteurs solaires thermiques pour eau chaude collective.",
      },
      {
        iconName: 'file-search',
        label: "Aide « études » et « investissement »",
        description: "L'ADEME finance les études amont (faisabilité) et les investissements selon des modalités forfaitaires ou proportionnelles au coût éligible.",
      },
    ],
    conditions: [
      "Maître d'ouvrage éligible : collectivité, EPCI, syndicat mixte, établissement public, bailleur social, opérateur de réseau",
      'Projet produisant de la chaleur renouvelable conformément aux critères ADEME applicables',
      'Dépôt du dossier avant tout engagement juridique des travaux',
      'Études amont (faisabilité, mesure du gisement) recommandées et finançables séparément',
      'Engagement sur des indicateurs de suivi (MWh EnR produits, taux EnR du réseau, etc.)',
    ],
    cumulableWith: ['Fonds vert', 'Aides collectivités', 'DETR / DSIL', 'Banque des Territoires', 'Aides régionales'],
    faq: [
      {
        question: 'Quel projet est éligible au Fonds chaleur ?',
        answer:
          "Les projets de production de chaleur renouvelable : chaufferies biomasse, réseaux de chaleur alimentés par renouvelable, géothermie, solaire thermique, récupération de chaleur fatale. Les conditions techniques (taux d'énergie renouvelable, performance) sont précisées par les méthodes ADEME applicables.",
      },
      {
        question: "Comment se calcule l'aide ?",
        answer:
          "Deux modalités selon le projet : (1) aide forfaitaire en €/MWh renouvelable produits sur la durée d'amortissement (méthode classique pour les réseaux de chaleur biomasse) ; (2) aide proportionnelle au coût éligible HT, plafonnée (méthode pour les projets innovants). Le calcul est précisé dans la décision de financement de l'ADEME.",
      },
      {
        question: "Le Fonds chaleur est-il cumulable avec les aides travaux ?",
        answer:
          "Oui, dans la limite des plafonds autorisés. Le cumul Fonds chaleur + aides travaux est fréquent sur les chaufferies biomasse et les réseaux de chaleur. La règle générale : le total des aides publiques ne doit pas excéder les plafonds autorisés.",
      },
    ],
    ctaLabel: 'Étudier mon projet chaleur EnR',
    ctaHref: '/contact?source=collectivites&besoin=fonds-chaleur',
    secondaryCta: { label: 'Réseaux de chaleur', href: '/collectivites/patrimoine/reseaux-chaleur' },
  },
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'optimisation-turpe',
    iconName: 'trending-down',
    eyebrow: "Tarif d'acheminement de l'électricité,",
    title: 'Optimisation TURPE du patrimoine public',
    shortLabel: 'Optimisation TURPE',
    lead: "La partie transport de vos factures d'électricité. Vous avez des dizaines de compteurs : en recalant les puissances souscrites, vous économisez sans aucun travaux.",
    amount: 'Économies récurrentes',
    amountLabel: 'Sur la part acheminement de chaque point de livraison',
    keyPoints: [
      {
        iconName: 'landmark',
        label: 'Revue exhaustive du parc',
        description: "Bâtiments scolaires (vides l'été), gymnases, mairies, armoires d'éclairage public : chaque compteur a son profil — et son potentiel d'optimisation.",
      },
      {
        iconName: 'gauge',
        label: 'Puissances calées sur les usages réels',
        description: "Les souscriptions héritées (anciens équipements, usages disparus) sont fréquentes dans le patrimoine public. On les compare aux appels de puissance réels et on recale.",
      },
      {
        iconName: 'sparkles',
        label: "Éclairage public : cas d'école",
        description: "Le passage au LED réduit fortement la puissance appelée par les armoires — mais les souscriptions sont rarement revues après travaux. C'est souvent le premier gisement d'économies.",
      },
      {
        iconName: 'gavel',
        label: 'Sans marché de travaux',
        description: "Les ajustements sont administratifs (paramètres des contrats), sans investissement : pas de procédure de marché, un simple échange avec le fournisseur titulaire.",
      },
    ],
    conditions: [
      'Inventaire des points de livraison du patrimoine (bâtiments + éclairage public)',
      'Analyse des 12 derniers mois de factures ou des courbes de charge disponibles',
      'Applicable quel que soit le fournisseur titulaire du marché de fourniture',
      'Recalage recommandé après chaque opération de rénovation (LED, CVC, isolation)',
    ],
    cumulableWith: ['Comparateur énergie collectivités', 'Aides collectivités', 'Fonds vert', 'Plan de réduction des consommations'],
    faq: [
      {
        question: "Cette optimisation nécessite-t-elle un marché public ?",
        answer:
          "Non : il s'agit d'un ajustement des paramètres des contrats existants (puissances souscrites, version tarifaire), ni de travaux ni d'un changement de fournisseur. La prestation d'audit peut s'inscrire dans nos accompagnements, référencés UGAP.",
      },
      {
        question: "Pourquoi l'éclairage public est-il prioritaire ?",
        answer:
          "Parce que les rénovations LED divisent la puissance appelée, mais que les puissances souscrites historiques restent souvent en place — la collectivité paie alors un transport calibré pour des lampes qui n'existent plus. La revue post-travaux des souscriptions est l'optimisation au meilleur rapport effort/gain.",
      },
      {
        question: 'Comment valoriser ces économies auprès des élus ?',
        answer:
          "Ce sont des économies de fonctionnement récurrentes, documentées compteur par compteur, sans investissement : elles se présentent très bien en commission finances. Notre livrable inclut un état avant/après par point de livraison, intégrable à une note de synthèse ou un rapport d'orientation budgétaire.",
      },
    ],
    ctaLabel: 'Demander une revue du parc',
    ctaHref: '/contact?source=collectivite&besoin=optimisation-turpe',
    secondaryCta: { label: 'Comparateur énergie collectivités', href: '/collectivites/comparateur-energie' },
  },
];

export function getAideCollectiviteStub(slug: string): AideStub | undefined {
  return AIDES_COLLECTIVITES.find((a) => a.slug === slug);
}
